import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { Section, SectionHeading } from "../common/Section";
import Reveal from "../common/Reveal";

/**
 * The delivery sequence, expanded from four phases to the seven the owner
 * actually describes: Discovery → Planning → Design → Development →
 * Testing → Deployment → Support.
 *
 * Nothing here is invented. The four original phase names survive as the
 * `phase` micro-label, all sixteen original feature bullets survive in
 * `items`, and the additional bullets and descriptions are taken from the
 * `process` arrays that already exist in the 25 real service data files
 * (src/pages/Services/  *  /data_*.jsx) — same language, same claims.
 *
 * Module scope, not inside the component, so it is never re-created and
 * can be used safely in effect dependencies.
 */
const PROCESS_STEPS = [
  {
    id: "discovery",
    title: "Discovery",
    phase: "Research & Analysis",
    description:
      "In-depth research ensures project success through comprehensive requirement gathering and market analysis.",
    items: ["Stakeholder Interviews", "Competitive Analysis", "User Research"],
  },
  {
    id: "planning",
    title: "Planning",
    phase: "Scope & Architecture",
    description:
      "Requirements become a roadmap: scope, solution architecture and the order the work gets built in.",
    items: ["Technical Feasibility", "ROI Assessment", "Solution Architecture"],
  },
  {
    id: "design",
    title: "Design",
    phase: "Design & Prototyping",
    description:
      "Transform concepts into interactive prototypes with user-centric design principles.",
    items: [
      "UX/UI Design",
      "Wireframing",
      "Interactive Prototypes",
      "Design Systems",
    ],
  },
  {
    id: "development",
    title: "Development",
    phase: "Build & Integration",
    description:
      "Agile development with continuous integration and quality assurance throughout.",
    items: ["Agile Methodology", "Code Review", "CI/CD Pipeline"],
  },
  {
    id: "testing",
    title: "Testing",
    phase: "Quality Assurance",
    description:
      "Rigorous testing before release: automated suites, performance tuning and a security review.",
    items: [
      "Quality Assurance",
      "Automated Testing",
      "Performance Optimization",
      "Security Audit",
    ],
  },
  {
    id: "deployment",
    title: "Deployment",
    phase: "Release & Handover",
    description:
      "A controlled release to production, followed by handover and training for the people who use it.",
    items: ["Production Deployment", "Phased Rollout", "Team Training"],
  },
  {
    id: "support",
    title: "Support",
    phase: "Maintenance & Optimization",
    description:
      "Ongoing support and optimization after launch: continuous monitoring and proactive maintenance.",
    items: [
      "Continuous Monitoring",
      "Proactive Maintenance",
      "Ongoing Optimization",
    ],
  },
];

const STEP_COUNT = PROCESS_STEPS.length;
const AUTOPLAY_MS = 6000;
const DL_EASE = [0.16, 1, 0.3, 1]; // matches --dl-ease in App.css

const pad = (n) => String(n).padStart(2, "0");
const tabId = (i) => `process-step-${PROCESS_STEPS[i].id}`;
const PANEL_ID = "process-step-detail";

const OurProcess = () => {
  const [active, setActive] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [inView, setInView] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);

  const prefersReduced = useReducedMotion();
  const trackRef = useRef(null);
  const tabRefs = useRef([]);

  // Only advance on its own when the section is on screen, the tab is
  // visible, nobody is pointing at or focused inside it, and the user has
  // not asked for reduced motion.
  const autoplay =
    isPlaying && inView && pageVisible && !hovered && !prefersReduced;

  useEffect(() => {
    if (!autoplay) return undefined;
    const timer = setTimeout(
      () => setActive((active + 1) % STEP_COUNT),
      AUTOPLAY_MS
    );
    return () => clearTimeout(timer);
  }, [autoplay, active]);

  useEffect(() => {
    const node = trackRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () =>
      document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Choosing a step is an explicit intent — stop the walkthrough so the
  // panel does not move on somebody who is reading it.
  const selectStep = (index) => {
    setActive(index);
    setIsPlaying(false);
  };

  const focusStep = (index) => {
    const next = (index + STEP_COUNT) % STEP_COUNT;
    selectStep(next);
    const node = tabRefs.current[next];
    if (node) node.focus();
  };

  const onTabKeyDown = (event) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        focusStep(active + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        focusStep(active - 1);
        break;
      case "Home":
        event.preventDefault();
        focusStep(0);
        break;
      case "End":
        event.preventDefault();
        focusStep(STEP_COUNT - 1);
        break;
      default:
        break;
    }
  };

  const step = PROCESS_STEPS[active];

  return (
    <Section tone="subtle" id="process">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:gap-12">
        <SectionHeading
          eyebrow="Our Process"
          title={
            <>
              How We Deliver{" "}
              <span className="bg-gradient-to-r from-brand-800 to-brand-500 bg-clip-text text-transparent">
                Excellence
              </span>
            </>
          }
          lead="A methodology we run on every project: seven steps from the first conversation to a live system we keep supporting."
        />

        <Reveal
          delay={90}
          className="flex items-center gap-4 lg:justify-end lg:pb-2"
        >
          <span className="font-mono text-xs tabular-nums tracking-[0.14em] text-ink-400">
            STEP <span className="text-ink-700">{pad(active + 1)}</span>
            <span className="mx-1 text-ink-300">/</span>
            {pad(STEP_COUNT)}
          </span>

          {/* No autoplay under reduced motion, so no control that lies about it. */}
          {prefersReduced ? null : (
            <button
              type="button"
              onClick={() => setIsPlaying((playing) => !playing)}
              aria-label={
                isPlaying
                  ? "Pause the process walkthrough"
                  : "Play the process walkthrough"
              }
              className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white px-3 py-1.5 font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-500 transition-colors duration-200 hover:border-brand-300 hover:text-brand-700"
            >
              {isPlaying ? (
                <Pause className="h-3 w-3" aria-hidden="true" />
              ) : (
                <Play className="h-3 w-3" aria-hidden="true" />
              )}
              {isPlaying ? "Pause" : "Play"}
            </button>
          )}
        </Reveal>
      </div>

      <div
        ref={trackRef}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        className="mt-12 lg:mt-16"
      >
        {/* The sequence itself: a vertical rail on small screens, a
            horizontal timeline from lg up. One tree, two orientations. */}
        <Reveal>
          <div
            role="tablist"
            aria-label="Our delivery process"
            onKeyDown={onTabKeyDown}
            className="flex flex-col lg:flex-row"
          >
            {PROCESS_STEPS.map((item, index) => {
              const isActive = index === active;
              const isDone = index < active;

              return (
                <div
                  key={item.id}
                  role="presentation"
                  className="relative min-w-0 lg:flex-1"
                >
                  {index < STEP_COUNT - 1 ? (
                    <span
                      aria-hidden="true"
                      className="absolute bottom-0 left-[17.5px] top-9 w-px bg-ink-200 lg:bottom-auto lg:left-11 lg:right-0 lg:top-[17.5px] lg:h-px lg:w-auto"
                    >
                      {/* Fill that follows progress — scales on Y when the
                          rail is vertical, on X when it is horizontal. */}
                      <span
                        className={`absolute inset-0 origin-top bg-brand-500 transition-transform duration-500 ease-out-expo lg:origin-left ${
                          isDone
                            ? "scale-100"
                            : "scale-y-0 lg:scale-x-0 lg:scale-y-100"
                        }`}
                      />
                    </span>
                  ) : null}

                  <button
                    type="button"
                    role="tab"
                    id={tabId(index)}
                    ref={(el) => {
                      tabRefs.current[index] = el;
                    }}
                    aria-selected={isActive}
                    aria-controls={PANEL_ID}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => selectStep(index)}
                    className="group relative flex w-full items-center gap-4 pb-5 text-left lg:flex-col lg:items-start lg:gap-3 lg:pb-0 lg:pr-6"
                  >
                    <span
                      className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg border font-mono text-[0.7rem] font-semibold tabular-nums transition-colors duration-200 ${
                        isActive
                          ? "border-brand-800 bg-brand-800 text-white"
                          : isDone
                          ? "border-brand-300 bg-brand-50 text-brand-700"
                          : "border-ink-200 bg-white text-ink-400 group-hover:border-brand-300 group-hover:text-brand-600"
                      }`}
                    >
                      {pad(index + 1)}
                    </span>
                    <span
                      className={`text-sm font-medium leading-snug transition-colors duration-200 ${
                        isActive
                          ? "text-ink-900"
                          : "text-ink-500 group-hover:text-ink-800"
                      }`}
                    >
                      {item.title}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </Reveal>

        {/* Detail for the selected step. The only animation in the section. */}
        <Reveal delay={90} className="mt-10 lg:mt-14">
          <div
            role="tabpanel"
            id={PANEL_ID}
            aria-labelledby={tabId(active)}
            tabIndex={0}
            className="rounded-panel border border-ink-200 bg-white shadow-card"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={step.id}
                initial={prefersReduced ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={prefersReduced ? { opacity: 1 } : { opacity: 0, y: -6 }}
                transition={{
                  duration: prefersReduced ? 0 : 0.3,
                  ease: DL_EASE,
                }}
                className="grid min-h-[23rem] gap-8 p-6 sm:p-9 lg:min-h-[17rem] lg:grid-cols-[0.95fr_1.05fr] lg:gap-14 lg:p-12"
              >
                <div className="flex items-start gap-5 sm:gap-6">
                  <span
                    aria-hidden="true"
                    className="font-mono text-4xl font-semibold leading-none tabular-nums text-brand-200 sm:text-5xl"
                  >
                    {pad(active + 1)}
                  </span>
                  <div className="min-w-0">
                    <p className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-brand-600">
                      {step.phase}
                    </p>
                    <h3 className="mt-3 font-bricolage text-2xl font-bold text-ink-900 sm:text-3xl">
                      {step.title}
                    </h3>
                    <p className="mt-4 max-w-[46ch] text-base leading-relaxed text-ink-500">
                      {step.description}
                    </p>
                  </div>
                </div>

                <div className="lg:border-l lg:border-ink-100 lg:pl-14">
                  <p className="font-mono text-[0.68rem] uppercase tracking-eyebrow text-ink-400">
                    In this step
                  </p>
                  <ul className="mt-4">
                    {step.items.map((entry) => (
                      <li
                        key={entry}
                        className="flex items-start gap-3 border-t border-ink-100 py-3 text-[0.95rem] text-ink-700 first:border-t-0 first:pt-0"
                      >
                        <span
                          aria-hidden="true"
                          className="mt-[9px] h-1 w-1 flex-none rounded-full bg-brand-500"
                        />
                        {entry}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </Section>
  );
};

export default OurProcess;
